import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Save, Loader2, Layers, ArrowUp, ArrowDown } from "lucide-react";

export default function RoleHierarchy() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/role-hierarchy").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  const setRank = (key, val) => setRows((rs) => rs.map((r) => r.key === key ? { ...r, rank: val } : r));

  const save = async () => {
    setSaving(true);
    try {
      const ranks = Object.fromEntries(rows.map((r) => [r.key, Number(r.rank) || 1]));
      const res = await api.put("/role-hierarchy", { ranks });
      setRows(res.data);
      toast.success("Hierarchy saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sorted = [...rows].sort((a, b) => (a.rank || 0) - (b.rank || 0));

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;

  return (
    <div className="space-y-5" data-testid="role-hierarchy-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><Layers size={24} className="text-emerald-600" /> Role Hierarchy</h1>
          <p className="text-sm text-slate-500 mt-1">Set a rank per role (1 = lowest). Higher-ranked roles see all leads & partners in their own downline.</p>
        </div>
        <button data-testid="hierarchy-save" onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save</button>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">Level</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3 w-40">Rank</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.key} data-testid={`hierarchy-row-${r.key}`} className="border-b border-slate-100 text-sm text-slate-700">
                <td className="px-4 py-3 text-slate-400 font-mono">{i + 1}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{r.label}</td>
                <td className="px-4 py-3">
                  <input data-testid={`hierarchy-rank-${r.key}`} type="number" min={1} value={r.rank} onChange={(e) => setRank(r.key, e.target.value)} className={`${inputCls} max-w-[110px]`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 flex items-center gap-1"><ArrowUp size={12} /> Higher rank = broader visibility. <ArrowDown size={12} /> Lower rank = only own leads/partners.</p>
    </div>
  );
}
